/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  SkillEditSuggestionChange,
  SkillEditSuggestionChange$inboundSchema,
} from "./skilleditsuggestionchange.js";

/**
 * The suggestion state.
 */
export const SkillEditSuggestionStatus = {
  Open: "open",
  Approved: "approved",
  Dismissed: "dismissed",
  Superseded: "superseded",
} as const;
/**
 * The suggestion state.
 */
export type SkillEditSuggestionStatus = ClosedEnum<
  typeof SkillEditSuggestionStatus
>;

/**
 * A proposed edit to an existing project skill.
 */
export type SkillEditSuggestion = {
  /**
   * Whether every proposed change still applies to the base version.
   */
  appliesCleanly: boolean;
  /**
   * When the suggestion was approved, when present.
   */
  approvedAt?: Date | undefined;
  /**
   * The user that approved the suggestion, when present.
   */
  approvedByUserId?: string | undefined;
  /**
   * The version the suggestion was generated from.
   */
  baseVersionId: string;
  /**
   * The separate changes proposed, each reviewable on its own.
   */
  changes: Array<SkillEditSuggestionChange>;
  /**
   * When the suggestion was created.
   */
  createdAt: Date;
  /**
   * Feedback records the suggestion was generated from.
   */
  feedbackCount: number;
  /**
   * Distinct sessions that reported the feedback behind the suggestion.
   */
  feedbackSessionCount: number;
  /**
   * The suggestion ID.
   */
  id: string;
  /**
   * The complete SKILL.md content produced by taking every proposed change.
   */
  proposedContent: string;
  /**
   * Why the edit was proposed, covering the suggestion as a whole.
   */
  rationale: string;
  /**
   * Scored sessions considered by the suggestion.
   */
  scoredSessionCount: number;
  /**
   * The user-facing skill name.
   */
  skillDisplayName: string;
  /**
   * The skill targeted by the suggestion.
   */
  skillId: string;
  /**
   * The canonical skill name.
   */
  skillName: string;
  /**
   * The suggestion state.
   */
  status: SkillEditSuggestionStatus;
  /**
   * When the suggestion was last updated.
   */
  updatedAt: Date;
};

/** @internal */
export const SkillEditSuggestionStatus$inboundSchema: z.ZodMiniEnum<
  typeof SkillEditSuggestionStatus
> = z.enum(SkillEditSuggestionStatus);

/** @internal */
export const SkillEditSuggestion$inboundSchema: z.ZodMiniType<
  SkillEditSuggestion,
  unknown
> = z.pipe(
  z.object({
    applies_cleanly: z.boolean(),
    approved_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    approved_by_user_id: z.optional(z.string()),
    base_version_id: z.string(),
    changes: z.array(SkillEditSuggestionChange$inboundSchema),
    created_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    feedback_count: z.int(),
    feedback_session_count: z.int(),
    id: z.string(),
    proposed_content: z.string(),
    rationale: z.string(),
    scored_session_count: z.int(),
    skill_display_name: z.string(),
    skill_id: z.string(),
    skill_name: z.string(),
    status: SkillEditSuggestionStatus$inboundSchema,
    updated_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
  }),
  z.transform((v) => {
    return remap$(v, {
      "applies_cleanly": "appliesCleanly",
      "approved_at": "approvedAt",
      "approved_by_user_id": "approvedByUserId",
      "base_version_id": "baseVersionId",
      "created_at": "createdAt",
      "feedback_count": "feedbackCount",
      "feedback_session_count": "feedbackSessionCount",
      "proposed_content": "proposedContent",
      "scored_session_count": "scoredSessionCount",
      "skill_display_name": "skillDisplayName",
      "skill_id": "skillId",
      "skill_name": "skillName",
      "updated_at": "updatedAt",
    });
  }),
);

export function skillEditSuggestionFromJSON(
  jsonString: string,
): SafeParseResult<SkillEditSuggestion, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => SkillEditSuggestion$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'SkillEditSuggestion' from JSON`,
  );
}
